import {
	Alert,
	Anchor,
	Badge,
	Button,
	Group,
	Image,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { AdminRequestDetail } from "@/features/applications/api/types";
import { StatusBadge } from "@/shared/ui/status-badge";

function fullName(
	name: string | null,
	surname: string | null,
	empty: string,
): string {
	return [name, surname].filter(Boolean).join(" ") || empty;
}

export function VerificationDocuments({
	detail,
	onImageError,
}: {
	detail: AdminRequestDetail;
	onImageError: () => void;
}) {
	const { t } = useTranslation();
	const latestRound = detail.rounds[0];

	return (
		<Paper p="md" radius="lg" withBorder>
			<Stack gap="sm">
				<Title order={4}>{t("verification.documents")}</Title>
				{!latestRound || latestRound.attachments.length === 0 ? (
					<Text c="dimmed">{t("verification.noAttachments")}</Text>
				) : (
					latestRound.attachments.map((attachment) => (
						<Stack key={attachment.id} gap={4}>
							<Text size="sm" c="dimmed">
								{attachment.contentType} · {(attachment.size / 1024).toFixed(1)}{" "}
								KB
							</Text>
							{attachment.contentType.startsWith("image/") ? (
								<Image
									src={attachment.url}
									radius="md"
									alt={t("verification.documents")}
									onError={onImageError}
								/>
							) : (
								<Anchor href={attachment.url} target="_blank" rel="noreferrer">
									{t("verification.openDocument")}
								</Anchor>
							)}
						</Stack>
					))
				)}
				{detail.requiredDocuments.length > 0 ? (
					<div>
						<Text fw={500} size="sm">
							{t("verification.requiredDocuments")}
						</Text>
						<ul>
							{detail.requiredDocuments.map((item) => (
								<li key={item}>
									<Text size="sm">{item}</Text>
								</li>
							))}
						</ul>
					</div>
				) : null}
			</Stack>
		</Paper>
	);
}

export function VerificationSubmitter({
	detail,
}: {
	detail: AdminRequestDetail;
}) {
	const { t } = useTranslation();
	const dash = t("common.dash");
	return (
		<Paper p="md" radius="lg" withBorder>
			<Stack gap="xs">
				<Title order={4}>{t("verification.submitter")}</Title>
				<Text>
					{fullName(detail.submitter.name, detail.submitter.surname, dash)}
				</Text>
				<Text size="sm">{detail.submitter.phone}</Text>
				{detail.submitter.email ? (
					<Text size="sm">{detail.submitter.email}</Text>
				) : null}
				{detail.subject ? (
					<>
						<Title order={5} mt="sm">
							{t("verification.child")}
						</Title>
						<Text>
							{fullName(detail.subject.name, detail.subject.surname, dash)}
						</Text>
						<Text size="sm">{detail.subject.phone}</Text>
					</>
				) : null}
				{detail.category ? (
					<Badge variant="light" w="fit-content">
						{detail.category.title}
					</Badge>
				) : null}
				{detail.adminComment ? (
					<Alert color="orange" title={t("verification.adminComment")}>
						{detail.adminComment}
					</Alert>
				) : null}
			</Stack>
		</Paper>
	);
}

export function VerificationDecision({
	awaitingDecision,
	onApprove,
	onDecline,
	onRework,
}: {
	awaitingDecision: boolean;
	onApprove: () => void;
	onDecline: () => void;
	onRework: () => void;
}) {
	const { t } = useTranslation();
	return (
		<Paper p="md" radius="lg" withBorder>
			<Stack gap="sm">
				<Title order={4}>{t("verification.decision")}</Title>
				{awaitingDecision ? (
					<Group>
						<Button color="green" onClick={onApprove}>
							{t("verification.approve")}
						</Button>
						<Button color="red" variant="light" onClick={onDecline}>
							{t("verification.decline")}
						</Button>
						<Button color="orange" variant="light" onClick={onRework}>
							{t("verification.rework")}
						</Button>
					</Group>
				) : (
					<Text c="dimmed" size="sm">
						{t("verification.noOpenDecision")}
					</Text>
				)}
			</Stack>
		</Paper>
	);
}

export function VerificationHistory({
	detail,
}: {
	detail: AdminRequestDetail;
}) {
	const { t } = useTranslation();
	const dash = t("common.dash");
	return (
		<Paper p="md" radius="lg" withBorder>
			<Stack gap="sm">
				<Title order={4}>{t("verification.history")}</Title>
				{detail.history.length === 0 ? (
					<Text c="dimmed" size="sm">
						{t("verification.noHistory")}
					</Text>
				) : (
					detail.history.map((item) => (
						<Stack key={`${item.round}-${item.decidedAt}`} gap={2}>
							<Group gap="xs">
								<Badge size="sm" variant="outline">
									{t("verification.round", { round: item.round })}
								</Badge>
								<StatusBadge
									status={
										item.decision === "APPROVED"
											? "APPROVED"
											: item.decision === "DECLINED"
												? "DECLINED"
												: "REOPENED"
									}
								/>
								<Text size="xs" c="dimmed">
									{new Date(item.decidedAt).toLocaleString("uk-UA")}
								</Text>
							</Group>
							{item.comment ? <Text size="sm">{item.comment}</Text> : null}
							{item.reviewedBy ? (
								<Text size="xs" c="dimmed">
									{t("verification.by", {
										name: fullName(
											item.reviewedBy.name,
											item.reviewedBy.surname,
											dash,
										),
									})}
								</Text>
							) : null}
						</Stack>
					))
				)}
			</Stack>
		</Paper>
	);
}
